/**
 * Utility functions for glucose unit defaults based on geographic location
 */

interface UserProfile {
  zip?: string
  address?: string
  email?: string
  clinic_name?: string
}

/**
 * Determine the appropriate glucose unit default based on user's geographic location
 * US users default to mg/dL, international users default to mmol/L
 */
export function getGeographicGlucoseDefault(user: UserProfile): 'mg/dL' | 'mmol/L' {
  if (!user) return 'mg/dL' // Default to US standard

  const { zip, address, email, clinic_name } = user

  // Check address for country indicators
  if (address) {
    const addressLower = address.toLowerCase()
    if (
      addressLower.includes('canada') ||
      addressLower.includes('ontario') ||
      addressLower.includes('quebec') ||
      addressLower.includes('british columbia') ||
      addressLower.includes(' bc ') ||
      addressLower.includes('alberta') ||
      addressLower.includes('manitoba') ||
      addressLower.includes('saskatchewan') ||
      addressLower.includes('nova scotia') ||
      addressLower.includes('new brunswick') ||
      addressLower.includes('newfoundland')
    ) {
      return 'mmol/L'
    }

    if (
      addressLower.includes('china') ||
      addressLower.includes('中国') ||
      addressLower.includes('beijing') ||
      addressLower.includes('北京') ||
      addressLower.includes('shanghai') ||
      addressLower.includes('上海')
    ) {
      return 'mmol/L'
    }

    // European indicators. NB: `includes('uk')` matched MILWAUKEE (and
    // Waukesha, Waukegan), seeding mmol/L for Wisconsin clinics — match "uk"
    // only as a whole word.
    if (
      /\buk\b/.test(addressLower) ||
      addressLower.includes('united kingdom') ||
      addressLower.includes('europe') ||
      addressLower.includes('germany') ||
      addressLower.includes('france') ||
      addressLower.includes('spain') ||
      addressLower.includes('italy')
    ) {
      return 'mmol/L'
    }
  }

  // Postcode SHAPE is only a hint, so it runs after the explicit country
  // names above: France, Germany, Spain and Italy all use 5-digit postcodes,
  // so testing the US pattern first returned mg/dL for them and made the
  // European branch above unreachable for exactly the countries it lists.
  if (zip && /^[0-9]{5}(-[0-9]{4})?$/.test(zip)) {
    return 'mg/dL' // US 5-digit zip codes
  }

  if (zip && /^[A-Z][0-9][A-Z][\s-]?[0-9][A-Z][0-9]$/i.test(zip)) {
    return 'mmol/L' // Canadian postal codes
  }

  // Check email domain patterns
  if (email) {
    if (email.endsWith('.ca') || email.includes('@qq.com')) {
      return 'mmol/L'
    }
  }

  // Default to US standard (mg/dL) for uncertain cases
  return 'mg/dL'
}

/**
 * Resolve the glucose unit preference from backend data.
 *
 * The full app-level precedence is:
 *   1. localStorage on this browser (handled at the call site — see authProvider)
 *   2. User's explicit preference from the backend
 *   3. Geographic default inferred from profile
 *   4. US default (mg/dL) as final fallback
 *
 * This function covers steps 2–4; callers must check localStorage first.
 */
export function resolveGlucoseUnitPreference(
  userPreference?: string,
  userProfile?: UserProfile,
): 'mg/dL' | 'mmol/L' {
  // Use explicit user preference if set
  if (userPreference === 'mg/dL' || userPreference === 'mmol/L') {
    return userPreference
  }

  // Fall back to geographic default
  if (userProfile) {
    return getGeographicGlucoseDefault(userProfile)
  }

  // Final fallback to US standard
  return 'mg/dL'
}
